import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAPI } from '../lib/hooks';
import { AppContext } from '../sporttia/AppContext';
import { APPPROVIDER_FLAGS } from '../lib/queryKeys';

const resource = '/flags';

/**
 * Feature flags del API (`GET /v8/flags`) que lee este gestor. El valor es el
 * nombre del flag en el backend, en kebab-case.
 */
export const Flag = {
    MANAGER_ROLES_PERMISSIONS: 'manager-roles-permissions',
} as const;

export type FlagName = (typeof Flag)[keyof typeof Flag];

type FlagState = { name: string; active: boolean };

type FlagsResponse = { data: FlagState[] };

type Request = ReturnType<typeof useAPI>[0];

/**
 * Consulta de `GET /v8/flags`. La comparten `useFlag` y `AppProvider` (misma
 * clave), así que sólo se pide una vez.
 */
export const flagsQueryOptions = (request: Request) => ({
    queryKey: [APPPROVIDER_FLAGS],
    queryFn: () => request<FlagsResponse>('GET', resource, undefined, 'v8'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
});

/** Si la respuesta de `GET /v8/flags` trae el flag encendido. */
export const isFlagActive = (
    response: FlagsResponse | undefined,
    name: FlagName,
): boolean =>
    (response?.data ?? []).some((flag) => flag.name === name && flag.active);

/**
 * Estado de un feature flag del API. Si `GET /v8/flags` falla, el flag queda
 * apagado sin avisar: es lo mismo que hace el API con un flag desconocido.
 *
 * El de los roles (`manager-roles-permissions`) ya está en `cxt.rolesEnabled`.
 */
export function useFlag(name: FlagName) {
    const { migrationApis } = useContext(AppContext)!;
    const [request] = useAPI(migrationApis);

    const { data, isPending } = useQuery(flagsQueryOptions(request));

    return { isActive: isFlagActive(data, name), isLoading: isPending };
}
